import { createTheme, alpha } from '@mui/material';

const BLUE = '#4f8cff';
const GREEN = '#16c784';
const RED = '#ea3943';
const AMBER = '#f5a623';
const PAPER = '#0f1628';
const PANEL_BORDER = 'rgba(255, 255, 255, 0.07)';

/** Dark, exchange-style theme (Binance/Bybit-inspired) with global component overrides. */
export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: BLUE, light: '#7aaaff', dark: '#2f6be0' },
    success: { main: GREEN },
    error: { main: RED },
    warning: { main: AMBER },
    background: { default: '#070b16', paper: PAPER },
    text: { primary: '#eaf0fb', secondary: '#8b97b0' },
    divider: PANEL_BORDER,
  },
  shape: { borderRadius: 14 },
  typography: {
    fontFamily: 'Inter, system-ui, Arial, sans-serif',
    h4: { fontWeight: 700, letterSpacing: -0.5 },
    h5: { fontWeight: 700, letterSpacing: -0.3 },
    h6: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    button: { fontWeight: 600, textTransform: 'none', letterSpacing: 0.1 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { scrollbarWidth: 'thin' },
        '.mono': { fontFamily: 'JetBrains Mono, ui-monospace, monospace' },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: alpha(PAPER, 0.8),
          backdropFilter: 'blur(8px)',
          border: `1px solid ${PANEL_BORDER}`,
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 16,
          backgroundColor: alpha(PAPER, 0.8),
          border: `1px solid ${PANEL_BORDER}`,
          transition: 'transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: alpha('#0a0f1e', 0.72),
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${PANEL_BORDER}`,
          backgroundImage: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: alpha('#0a0f1e', 0.85),
          backgroundImage: 'none',
          borderRight: `1px solid ${PANEL_BORDER}`,
          backdropFilter: 'blur(12px)',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 12, paddingInline: 18, paddingBlock: 8 },
        containedPrimary: {
          background: `linear-gradient(135deg, ${BLUE}, #6aa0ff)`,
          boxShadow: `0 8px 24px ${alpha(BLUE, 0.35)}`,
          '&:hover': { background: `linear-gradient(135deg, #5d97ff, #7aaaff)` },
        },
        containedSuccess: {
          background: `linear-gradient(135deg, ${GREEN}, #1fe3a0)`,
          boxShadow: `0 8px 24px ${alpha(GREEN, 0.3)}`,
        },
        containedError: {
          background: `linear-gradient(135deg, ${RED}, #ff5663)`,
          boxShadow: `0 8px 24px ${alpha(RED, 0.3)}`,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          border: `1px solid ${PANEL_BORDER}`,
          color: '#8b97b0',
          '&.Mui-selected': {
            color: '#fff',
            backgroundColor: alpha(BLUE, 0.18),
            borderColor: alpha(BLUE, 0.5),
            '&:hover': { backgroundColor: alpha(BLUE, 0.26) },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: alpha('#0a0f1e', 0.6),
          '& fieldset': { borderColor: PANEL_BORDER },
          '&:hover fieldset': { borderColor: alpha(BLUE, 0.45) },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: 8 },
        sizeSmall: { height: 22 },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: PANEL_BORDER },
        head: {
          color: '#7c89a3',
          fontWeight: 600,
          fontSize: 12,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
          backgroundColor: alpha('#0a0f1e', 0.5),
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { transition: 'background-color 120ms ease' },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          marginInline: 10,
          marginBlock: 3,
          '&.Mui-selected': {
            backgroundColor: alpha(BLUE, 0.16),
            '&:hover': { backgroundColor: alpha(BLUE, 0.22) },
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: '#1a2236', border: `1px solid ${PANEL_BORDER}`, fontSize: 12 },
      },
    },
  },
});
